import React from 'react';

/**
 * Host suffixes for every non-production deployment of the app. One suffix
 * catches two unrelated families: the dev app hosts (`app.dev.runcofactor.com`
 * and per-org `*.app.dev.runcofactor.com`, from `infra/components/app-hosts.ts`)
 * and dev-stage customer sandboxes (`<id>.env.dev.runcofactor.com`). Portless
 * gives each local worktree a `.localhost` name.
 *
 * `config.ts` classifies these same hosts separately for API-URL resolution; a
 * new stage would have to be taught to both.
 */
const DEV_HOSTNAME_SUFFIXES = ['.dev.runcofactor.com', '.localhost'] as const;

/**
 * Product display content — the single source of truth for the product name
 * and brand chrome shown in the UI. Route user-facing copy through `BRAND`
 * instead of hardcoding "Cofactor".
 */
export const BRAND = {
  name: 'Cofactor',
  tagline: 'Ship fast. And don’t break things.',
  termsUrl: 'https://runcofactor.com/terms',
  privacyUrl: 'https://runcofactor.com/privacy',
  cliCommand: 'cofactor',
  cliPackage: '@runcofactor/cli',
} as const;

/**
 * Favicon for non-production hosts, shown in place of the brand icon so a dev
 * tab is never mistaken for production. It is deliberately brand-independent —
 * on dev, "this is dev" is the fact worth reading off a 16px tab, and the mark
 * on a solid dark tile stays distinguishable from the brand icon at that size.
 */
const DEV_FAVICON = { href: '/favicon-dev.svg', type: 'image/svg+xml' };

/**
 * Whether a hostname belongs to a non-production deployment. Resolved from the
 * hostname rather than a build-time env var because one prebuilt `dist` is
 * reused across hosts (see `config.ts`), so a baked-in flag would label the
 * wrong deployments.
 *
 * The bare local names have no leading dot, so they cannot match a suffix and
 * are compared exactly. `127.0.0.1` is included because the local dev server is
 * reachable under it and `config.ts`'s `IS_LOCAL` already counts it as local.
 */
function isDevHostname(hostname: string): boolean {
  if (hostname === 'localhost' || hostname === '127.0.0.1') return true;
  return DEV_HOSTNAME_SUFFIXES.some((suffix) => hostname.endsWith(suffix));
}

/**
 * Swaps in {@link DEV_FAVICON} on non-production hosts, so a dev tab is never
 * mistaken for production. On production hosts this is a no-op: `index.html`
 * already ships the Cofactor favicon, and `useFaviconBadge` falls back to
 * `link.href` when `data-base-href` was never set.
 */
export function useFavicon(): void {
  React.useEffect(() => {
    if (!isDevHostname(window.location.hostname)) return;
    const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) return;
    const resolved = new URL(DEV_FAVICON.href, document.baseURI).href;
    link.type = DEV_FAVICON.type;
    link.dataset.baseHref = resolved;
    link.href = resolved;
  }, []);
}
